import axiosInstance, { getErrorMessage, getValidationErrors } from '../utils/axios';

/**
 * Contact Service
 * Handles all API calls related to contact management
 */
class ContactService {
  
  // Base endpoint for contacts
  baseEndpoint = '/contacts';

  /**
   * Create a new contact (used by contact form)
   * @param {Object} contactData - Contact form data
   * @returns {Promise<Object>} API response with created contact
   */
  async createContact(contactData) {
    try {
      const response = await axiosInstance.post(this.baseEndpoint, contactData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Contact created successfully'
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
        validationErrors: getValidationErrors(error),
        statusCode: error.response?.status
      };
    }
  }

  /**
   * Get all contacts with filtering and pagination
   * @param {Object} filters - Filter parameters
   * @param {Object} pagination - Pagination parameters
   * @returns {Promise<Object>} API response with contacts list
   */
  async getAllContacts(filters = {}, pagination = {}) {
    try {
      const params = {
        ...filters,
        ...pagination
      };

      const response = await axiosInstance.get(this.baseEndpoint, { params });
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
        statusCode: error.response?.status
      };
    }
  }

  /**
   * Get contact by ID
   * @param {string} id - Contact ID
   * @returns {Promise<Object>} API response with contact data
   */
  async getContactById(id) {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/${id}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
        statusCode: error.response?.status
      };
    }
  }

  /**
   * Update contact by ID
   * @param {string} id - Contact ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} API response with updated contact
   */
  async updateContact(id, updateData) {
    try {
      const response = await axiosInstance.put(`${this.baseEndpoint}/${id}`, updateData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Contact updated successfully'
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
        validationErrors: getValidationErrors(error),
        statusCode: error.response?.status
      };
    }
  }

  /**
   * Delete contact by ID
   * @param {string} id - Contact ID
   * @returns {Promise<Object>} API response
   */
  async deleteContact(id) {
    try {
      const response = await axiosInstance.delete(`${this.baseEndpoint}/${id}`);
      return {
        success: true,
        message: response.data.message || 'Contact deleted successfully',
        data: response.data.data
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
        statusCode: error.response?.status
      };
    }
  }

  /**
   * Add note to contact
   * @param {string} id - Contact ID
   * @param {Object} noteData - Note data (content, addedBy)
   * @returns {Promise<Object>} API response
   */
  async addNote(id, noteData) {
    try {
      const response = await axiosInstance.post(`${this.baseEndpoint}/${id}/notes`, noteData);
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Note added successfully'
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
        validationErrors: getValidationErrors(error),
        statusCode: error.response?.status
      };
    }
  }

  /**
   * Mark contact as resolved
   * @param {string} id - Contact ID
   * @param {string} resolvedBy - Who resolved the contact
   * @returns {Promise<Object>} API response
   */
  async resolveContact(id, resolvedBy) {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${id}/resolve`, { resolvedBy });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Contact marked as resolved'
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
        statusCode: error.response?.status
      };
    }
  }

  /**
   * Get contact statistics
   * @returns {Promise<Object>} API response with stats
   */
  async getContactStats() {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/stats`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
        statusCode: error.response?.status
      };
    }
  }

  /**
   * Bulk update contact status
   * @param {Array} ids - Array of contact IDs
   * @param {string} status - New status
   * @param {string} updatedBy - Who updated the contacts
   * @returns {Promise<Object>} API response
   */
  async bulkUpdateStatus(ids, status, updatedBy = null) {
    try {
      const response = await axiosInstance.post(`${this.baseEndpoint}/bulk-update`, {
        ids,
        status,
        updatedBy
      });
      return {
        success: true,
        data: response.data.results,
        message: response.data.message || 'Bulk update completed'
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
        validationErrors: getValidationErrors(error),
        statusCode: error.response?.status
      };
    }
  }

  /**
   * Export contacts
   * @param {Object} filters - Filter parameters
   * @param {string} format - Export format (json)
   * @returns {Promise<Object>} API response with export data
   */
  async exportContacts(filters = {}, format = 'json') {
    try {
      const params = {
        ...filters,
        format
      };

      const response = await axiosInstance.get(`${this.baseEndpoint}/export`, { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
        statusCode: error.response?.status
      };
    }
  }

  /**
   * Search contacts
   * @param {string} searchTerm - Search term
   * @param {Object} additionalFilters - Additional filters
   * @returns {Promise<Object>} API response with search results
   */
  async searchContacts(searchTerm, additionalFilters = {}) {
    const filters = {
      search: searchTerm,
      ...additionalFilters
    };

    return this.getAllContacts(filters);
  }

  /**
   * Get contacts by status
   * @param {string} status - Contact status
   * @param {Object} pagination - Pagination parameters
   * @returns {Promise<Object>} API response with filtered contacts
   */
  async getContactsByStatus(status, pagination = {}) {
    const filters = { status };
    return this.getAllContacts(filters, pagination);
  }

  /**
   * Get contacts by region
   * @param {string} region - Contact region
   * @param {Object} pagination - Pagination parameters
   * @returns {Promise<Object>} API response with filtered contacts
   */
  async getContactsByRegion(region, pagination = {}) {
    const filters = { region };
    return this.getAllContacts(filters, pagination);
  }

  /**
   * Get urgent contacts
   * @param {Object} pagination - Pagination parameters
   * @returns {Promise<Object>} API response with urgent contacts
   */
  async getUrgentContacts(pagination = {}) {
    const filters = { priority: 'urgent', resolved: false };
    return this.getAllContacts(filters, pagination);
  }

  /**
   * Get recent contacts
   * @param {number} days - Number of days to look back
   * @param {Object} pagination - Pagination parameters
   * @returns {Promise<Object>} API response with recent contacts
   */
  async getRecentContacts(days = 7, pagination = {}) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const filters = {
      startDate: startDate.toISOString()
    };
    
    return this.getAllContacts(filters, pagination);
  }

  /**
   * Check API health
   * @returns {Promise<Object>} API health status
   */
  async checkHealth() {
    try {
      const response = await axiosInstance.get('/health');
      return {
        success: true,
        data: response.data,
        isHealthy: response.status === 200
      };
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
        isHealthy: false,
        statusCode: error.response?.status
      };
    }
  }
}

// Create and export a singleton instance
const contactService = new ContactService();
export default contactService;

// Also export the class for testing purposes
export { ContactService };
